import { trackEvent, getValueBand } from "../utils";

export const LEAD_FORM_NAME = "cash_buyer_comparison";

// e.g. "Address Input" -> "address_input"
export const toTrackingName = (name: string) =>
  name
    .split(" ")
    .map((word) => word.toLowerCase())
    .join("_");

// ---- step view ----
export const trackStepView = (params: {
  stepIndex: number;
  stepName: string;
  totalSteps: number;
}) => {
  const progressPercent = Math.round(
    ((params.stepIndex + 1) / params.totalSteps) * 100,
  );
  trackEvent("lead_form_step_view", {
    form_name: LEAD_FORM_NAME,
    step_number: params.stepIndex + 1,
    step_name: params.stepName,
    progress_percent: progressPercent,
  });
};

// ---- validation error ----
export const defaultDetectFailedField = (
  _responses: Record<string, unknown>,
) => ({
  fieldName: "required_field",
  errorType: "missing_required_field",
});

export const detectContactDetailsFailedField = (
  responses: Record<string, any>,
) => {
  if (!responses.phone_number) {
    return { fieldName: "phone_number", errorType: "missing_required_field" };
  } else if (!responses.email) {
    return { fieldName: "email", errorType: "invalid_email" };
  } else if (!responses.first_name) {
    return { fieldName: "first_name", errorType: "missing_required_field" };
  } else if (!responses.last_name) {
    return { fieldName: "last_name", errorType: "missing_required_field" };
  } else if (!responses.title) {
    return { fieldName: "title", errorType: "missing_required_field" };
  }
  return defaultDetectFailedField(responses);
};

export const trackValidationError = (params: {
  stepIndex: number;
  stepName: string;
  fieldName: string;
  errorType: string;
  timeOnStepSeconds: number;
}) => {
  trackEvent("lead_form_validation_error", {
    form_name: LEAD_FORM_NAME,
    step_number: params.stepIndex + 1,
    step_name: params.stepName,
    field_name: params.fieldName,
    error_type: params.errorType,
    time_on_step_seconds: params.timeOnStepSeconds,
  });
};

// ---- step complete ----
export const getQualificationParams = (responses: Record<string, any>) => ({
  is_inherited: responses.inherited || "unknown",
  property_condition: responses.property_condition || "unknown",
  has_serious_issues: responses.serious_issues || "unknown",
  occupancy_status: responses.occupation_status || "unknown",
  estimated_value_band: getValueBand(responses.user_estimated_value),
});

export const trackStepComplete = (params: {
  stepIndex: number;
  stepName: string;
  nextStepName: string;
  totalSteps: number;
  timeOnStepSeconds: number;
  extraParams?: Record<string, any>;
}) => {
  const progressPercent = Math.round(
    ((params.stepIndex + 1) / params.totalSteps) * 100,
  );
  trackEvent("lead_form_step_complete", {
    form_name: LEAD_FORM_NAME,
    step_number: params.stepIndex + 1,
    step_name: params.stepName,
    next_step_name: params.nextStepName,
    progress_percent: progressPercent,
    time_on_step_seconds: params.timeOnStepSeconds,
    ...(params.extraParams || {}),
  });
};

// ---- final submit ----
export const buildSubmitParams = (values: { [key: string]: any }) => {
  const params: Record<string, any> = {
    form_name: LEAD_FORM_NAME,
    lead_type: LEAD_FORM_NAME,
  };

  if (values.property_type) params.property_type = values.property_type;
  if (values.freehold_leasehold)
    params.tenure =
      values.freehold_leasehold === "freehold" ? "freehold" : "leasehold";
  if (values.bedrooms) params.bedrooms = values.bedrooms;
  if (values.epc_rating) params.epc_rating = values.epc_rating;
  if (values.postcode) {
    const postcode = values.postcode as string;
    params.postcode_area = postcode.split(" ")[0];
    params.postcode_district =
      postcode.split(" ")[0] +
      " " +
      (postcode.split(" ")[1] ? postcode.split(" ")[1][0] : "");
  }
  if (values.inherited) params.is_inherited = values.inherited;
  if (values.property_condition)
    params.property_condition = values.property_condition;
  if (values.serious_issues)
    params.has_serious_issues = values.serious_issues;
  if (values.serious_issues_type)
    params.issue_type = values.serious_issues_type;
  if (values.occupation_status)
    params.occupancy_status = values.occupation_status;
  if (values.cash_sale_reason)
    params.main_cash_sale_reason = values.cash_sale_reason;
  if (values.sale_reason) params.reason_for_selling = values.sale_reason;
  if (values.cash_sale_understanding)
    params.below_market_acceptance =
      values.cash_sale_understanding === "yes"
        ? "confirmed"
        : "not_confirmed";
  if (values.timeframe) params.sale_timeframe = values.timeframe;
  if (values.user_estimated_value)
    params.estimated_value_band = getValueBand(values.user_estimated_value);

  return params;
};

export const trackLeadFormSubmit = (values: { [key: string]: any }) => {
  trackEvent("lead_form_submit", buildSubmitParams(values));
};

// ---- submission/comparison view ----
export const trackComparisonResultView = (params: {
  eligibleBuyersCount: number;
  highestTrustScore: number;
  topBuyerId: string | number;
}) => {
  trackEvent("comparison_result_view", {
    form_name: LEAD_FORM_NAME,
    eligible_buyers_count: params.eligibleBuyersCount,
    highest_trust_score: params.highestTrustScore,
    top_buyer_id: params.topBuyerId,
  });
};

// ---- buyer offer request ----
export const trackBuyerOfferRequest = (params: {
  buyerId: string;
  buyerPosition: number;
  trustScore: number;
}) => {
  trackEvent("buyer_offer_request", {
    form_name: LEAD_FORM_NAME,
    buyer_id: params.buyerId,
    buyer_position: params.buyerPosition,
    trust_score: params.trustScore,
  });
};
